import React, { useState, useRef, useEffect, useMemo } from 'react';
import PlayIcon from '../icons/PlayIcon';
import PauseIcon from '../icons/PauseIcon';
import defaultImg from '@/assets/images/web/BlogFailureLmage.png';
import checkmarkImg from '@/assets/images/Multimodal.png';
import copyIconImg from '@/assets/images/web/normal.png';
import ToastUtils from '@/utils/ToastUtils';
import s from './VideoPlayerContent.module.scss';
import { Tooltip } from 'antd';

/**
 * 视频播放器内容组件（播客）
 * 包含音频播放器 + 对话脚本
 */
const VideoPlayerContent = ({ data }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [progressValue, setProgressValue] = useState(0);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [hoverInfo, setHoverInfo] = useState(null); // { time, percent }
    const [isDragging, setIsDragging] = useState(false);
    const [clickedIndex, setClickedIndex] = useState(null); // 用户点击的项索引
    const [isCopied, setIsCopied] = useState(false); // 是否已复制
    const audioRef = useRef(null);
    const progressBarRef = useRef(null);
    const speedMenuRef = useRef(null);

    // 监听播放时间变化，更新进度条
    useEffect(() => {
        if (currentTime > 0 && duration > 0) {
            setProgressValue((currentTime / duration) * 100);
        } else {
            setProgressValue(0);
        }
    }, [currentTime, duration]);

    // 监听音频URL变化
    useEffect(() => {
        if (data.audioUrl && audioRef.current) {
            audioRef.current.src = data.audioUrl;
            audioRef.current.load();
        }
    }, [data.audioUrl]);

    // 播放/暂停切换
    const togglePlay = () => {
        if (!audioRef.current) return;
        
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play().catch(err => {
                console.error('播放失败:', err);
            });
        }
    };

    // 快进30秒
    const skipForward = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.min(
                audioRef.current.currentTime + 30,
                duration
            );
        }
    };

    // 快退15秒
    const skipBackward = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(
                audioRef.current.currentTime - 15,
                0
            );
        }
    };

    // 切换倍速菜单显示
    const toggleSpeedMenu = () => {
        setShowSpeedMenu(!showSpeedMenu);
    };

    // 设置倍速
    const setSpeed = (rate) => {
        setPlaybackRate(rate);
        if (audioRef.current) {
            audioRef.current.playbackRate = rate;
        }
        setShowSpeedMenu(false);
    };

    // 点击外部关闭倍速菜单
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (speedMenuRef.current && !speedMenuRef.current.contains(event.target)) {
                setShowSpeedMenu(false);
            }
        };

        if (showSpeedMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSpeedMenu]);

    // 进度条拖动/点击
    const seekByClientX = (clientX) => {
        if (!duration || !progressBarRef.current) return;
        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
        const newTime = percent * duration;
        if (audioRef.current) {
            audioRef.current.currentTime = newTime;
        }
        setCurrentTime(newTime);
    };

    const handleSeek = (e) => {
        seekByClientX(e.clientX);
    };

    // 进度条 hover 显示时间
    const handleProgressHover = (e) => {
        if (!duration || !progressBarRef.current) return;
        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        const time = percent * duration;
        setHoverInfo({ time, percent });
    };

    const handleProgressLeave = () => {
        setHoverInfo(null);
    };

    // 进度条拖动
    const handleProgressMouseDown = (e) => {
        if (!duration) return;
        setIsDragging(true);
        seekByClientX(e.clientX);
        e.preventDefault();
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            seekByClientX(e.clientX);
        };

        const handleMouseUp = (e) => {
            seekByClientX(e.clientX);
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, duration]);

    // 时间更新
    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const newTime = audioRef.current.currentTime;
            setCurrentTime(newTime);
            if (clickedIndex !== null && Math.abs(newTime - (data.transcript?.[clickedIndex]?.startTime || 0)) > 0.5) {
                setClickedIndex(null);
            }
        }
    };

    // 加载元数据
    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    // 播放事件
    const handlePlay = () => {
        setIsPlaying(true);
    };

    // 暂停事件
    const handlePause = () => {
        setIsPlaying(false);
    };

    // 播放结束
    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    // 格式化时间
    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // 跳转到指定时间点
    const jumpToTime = (time, index) => {
        if (audioRef.current) {
            // 确保时间值是数字类型，避免精度问题
            const targetTime = Number(time) || 0;
            audioRef.current.currentTime = targetTime;
            setCurrentTime(targetTime);
            // 设置点击的索引，确保立即高亮对应项
            if (index !== undefined) {
                setClickedIndex(index);
            }
        }
    };

    // 复制对话脚本
    const handleCopyScript = () => {
        if (!data.transcript) {
            ToastUtils.showToast('暂无对话脚本');
            return;
        }

        const scriptText = data.transcript.map((item) => item.text).join('\n');

        navigator.clipboard
            .writeText(scriptText)
            .then(() => {
                setIsCopied(true);
            })
            .catch(() => {});
    };

    // 6秒后恢复复制图标
    useEffect(() => {
        if (isCopied) {
            const timer = setTimeout(() => {
                setIsCopied(false);
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [isCopied]);

    // 清洗标题：去掉markdown **，去掉“播客总标题”前缀（含括号/限制字数等说明）
    const cleanTitle = (() => {
        const raw = data.title || '';
        const noMd = raw.replace(/\*\*/g, '').trim();
        const noPrefix = noMd.replace(/^播客总标题.*?：\s*/,'').trim();
        return noPrefix || '播客';
    })();

    // 计算当前应该高亮的索引：根据时间区间判断
    // 每个项从自己的 startTime 开始，到下一项的 startTime 减1秒结束
    const activeIndex = useMemo(() => {
        if (!data.transcript?.length) return -1;
        
        // 如果用户点击了某个项，优先使用点击的索引
        if (clickedIndex !== null) {
            return clickedIndex;
        }
        
        // 确保 currentTime 是数字类型
        const time = Number(currentTime) || 0;
        
        // 如果时间为 0 且没有点击任何项，不高亮任何项
        if (time === 0) {
            return -1;
        }
        
        // 遍历所有项，找到 currentTime 所在的区间
        for (let i = 0; i < data.transcript.length; i++) {
            const itemStartTime = Number(data.transcript[i].startTime) || 0;
            const nextItem = data.transcript[i + 1];
            
            // 判断 currentTime 是否在当前项的区间内
            // 每个项从自己的 startTime 开始，到下一项的 startTime 减1秒结束
            if (time >= itemStartTime) {
                if (nextItem) {
                    // 有下一项：当前项在 [itemStartTime, nextItemStartTime - 1] 区间内
                    const nextItemStartTime = Number(nextItem.startTime) || 0;
                    // 当前时间小于下一项的开始时间
                    // 例如：第一项 [7, 25]，当 currentTime = 7 时，7 >= 7 && 7 < 26，应该匹配第一项
                    // 当 currentTime = 25 时，25 >= 7 && 25 < 26，应该匹配第一项
                    // 当 currentTime = 26 时，26 >= 7 但 26 < 26 为 false，继续循环匹配第二项
                    if (time < nextItemStartTime) {
                        return i;
                    }
                } else {
                    // 最后一项，只要 currentTime >= itemStartTime 就匹配
                    return i;
                }
            }
        }
        
        // 如果都不匹配，返回 -1（不高亮任何项）
        return -1;
    }, [data.transcript, currentTime, duration, clickedIndex]);

    return (
        <div className={s['video-player-container']}>
            {/* 视频播放器区域 */}
            <div className={s['video-player-wrapper']}>
                {/* 播客封面 */}
                <div className={s['podcast-cover']}>
                    <img 
                        src={data.thumbnail || defaultImg} 
                        alt={cleanTitle || '播客封面'}
                        className={s['podcast-cover-image']}
                        onError={(e) => {
                            // 如果图片加载失败，使用默认图片
                            e.target.src = defaultImg;
                        }}
                    />
                </div>
                
                {/* 播客标题 */}
                <div className={s['podcast-title']}>
                    {cleanTitle}
                </div>
                
                {/* 进度条 */}
                <div className={s['progress-bar-wrapper']}>
                    <span className={s['time-display']}>{formatTime(currentTime)}</span>
                    <div 
                        className={s['progress-bar']} 
                        ref={progressBarRef}
                        onClick={handleSeek}
                        onMouseMove={handleProgressHover}
                        onMouseLeave={handleProgressLeave}
                        onMouseDown={handleProgressMouseDown}
                    >
                        {hoverInfo && (
                            <div 
                                className={s['progress-tooltip']} 
                                style={{ left: `${hoverInfo.percent * 100}%` }}
                            >
                                {formatTime(hoverInfo.time)}
                            </div>
                        )}
                        <div 
                            className={s['progress-fill']} 
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                        >
                            <div className={s['progress-handle']}></div>
                        </div>
                    </div>
                    <span className={s['time-display']}>{formatTime(duration)}</span>
                </div>

                {/* 播放控制按钮 */}
                <div className={s['player-controls']}>
                    {/* 左侧：倍速按钮 */}
                    <div className={s['speed-control']} ref={speedMenuRef}>
                        <button 
                            className={`${s['speed-btn']} ${showSpeedMenu ? s['active'] : ''}`}
                            onClick={toggleSpeedMenu}
                        >
                            {playbackRate}x
                        </button>
                        {showSpeedMenu && (
                            <div className={s['speed-menu']}>
                                <div 
                                    className={`${s['speed-option']} ${playbackRate === 0.75 ? s['active'] : ''}`}
                                    onClick={() => setSpeed(0.75)}
                                >
                                    0.75x
                                </div>
                                <div 
                                    className={`${s['speed-option']} ${playbackRate === 1 ? s['active'] : ''}`}
                                    onClick={() => setSpeed(1)}
                                >
                                    1x
                                </div>
                                <div 
                                    className={`${s['speed-option']} ${playbackRate === 1.25 ? s['active'] : ''}`}
                                    onClick={() => setSpeed(1.25)}
                                >
                                    1.25x
                                </div>
                                <div 
                                    className={`${s['speed-option']} ${playbackRate === 1.5 ? s['active'] : ''}`}
                                    onClick={() => setSpeed(1.5)}
                                >
                                    1.5x
                                </div>
                                <div 
                                    className={`${s['speed-option']} ${playbackRate === 2 ? s['active'] : ''}`}
                                    onClick={() => setSpeed(2)}
                                >
                                    2x
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 中间：播放控制按钮组（绝对居中）*/}
                    <div className={s['center-controls']}>
                        <button 
                            className={s['skip-btn']} 
                            onClick={skipBackward}
                            title="倒退15秒"
                        >
                            <i className="iconfont icon-daotui15miao" style={{ fontSize: '22px' }}></i>
                        </button>
                        <button 
                            className={s['play-btn']} 
                            onClick={togglePlay}
                            title={isPlaying ? '暂停' : '播放'}
                        >
                            {isPlaying ? (
                                <PauseIcon style={{ fontSize: '22px' }} />
                            ) : (
                                <PlayIcon style={{ fontSize: '18px' }} />
                            )}
                        </button>
                        <button 
                            className={s['skip-btn']} 
                            onClick={skipForward}
                            title="快进30秒"
                        >
                            <i className="iconfont icon-kuaijin30miao" style={{ fontSize: '22px' }}></i>
                        </button>
                    </div>
                </div>

                {/* 隐藏的 audio 元素 */}
                <audio
                    ref={audioRef}
                    src={data.audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    style={{ display: 'none' }}
                />
            </div>

            {/* 对话脚本区域 */}
            <div className={s['transcript-section']}>
                <div className={s['transcript-header']}>
                    <div className={s['section-icon']}>
                        <i className="iconfont icon-duihuajiaoben"></i>
                    </div>
                    <h3 className={s['section-title']}>对话脚本</h3>
                    <Tooltip title={isCopied ? '已复制' : '复制'} placement="top">
                        <button 
                            className={s['copy-btn']} 
                            onClick={handleCopyScript}
                        >
                            {isCopied ? (
                                <img src={checkmarkImg} alt="已复制" style={{ width: '28px', height: '28px', display: 'block' }} />
                            ) : (
                                <img src={copyIconImg} alt="复制" style={{ width: '20px', height: '20px', display: 'block' }} />
                            )}
                        </button>
                    </Tooltip>
                </div>

                {/* 对话脚本列表 */}
                <div className={s['transcript-list']}>
                    {data.transcript?.map((item, index) => {
                        const itemStartTime = item.startTime || 0;
                        // 使用 activeIndex 来判断是否高亮，确保点击时能正确高亮对应的项
                        const isActive = index === activeIndex;
                        
                        return (
                            <div 
                                key={index} 
                                className={`${s['transcript-item']} ${isActive ? s['active'] : ''}`}
                                onClick={() => jumpToTime(itemStartTime, index)}
                                onMouseDown={(e) => e.preventDefault()} // 阻止鼠标按下时的默认行为，防止文本选择
                            >
                                <div className={s['time-marker']}>
                                    <span className={s['time-dot']}></span>
                                    <span className={s['time-text']}>{item.time}</span>
                                </div>
                                <div className={s['transcript-text']}>
                                    {item.text}
                                </div>
                            </div>
                        );
                    }) || (
                        <div className={s['no-transcript']}>
                            暂无对话脚本
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideoPlayerContent;

